import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { ExportButton } from '../components/ExportButton';
import { COLUMN_PRESETS } from '../services/exportService';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Search, Filter, FileText, ChevronLeft, ChevronRight, X, ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const ENTITY_TYPES = [
  { value: '', label: 'Todas entidades' },
  { value: 'control_act', label: 'Ato de Controle' },
  { value: 'action_plan', label: 'Plano de Ação' },
  { value: 'evidence', label: 'Evidência' },
  { value: 'response', label: 'Resposta' },
  { value: 'user', label: 'Usuário' },
];

const ACTIONS = [
  { value: '', label: 'Todas ações' },
  { value: 'CREATE', label: 'Criação' },
  { value: 'UPDATE', label: 'Atualização' },
  { value: 'TRANSITION', label: 'Transição' },
  { value: 'UPLOAD', label: 'Upload' },
  { value: 'VALIDATE', label: 'Validação' },
  { value: 'UPDATE_STATUS', label: 'Mudança de Status' },
  { value: 'UPDATE_ROLE', label: 'Mudança de Perfil' },
];

export const AuditPage = () => {
  const { api, user } = useAuth();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);

  const [filters, setFilters] = useState({
    entity_type: '',
    action: '',
    user_id: '',
    entity_id: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);

  const canVerifyChain = ['ADMIN_ESTADUAL', 'CGE', 'CONTROLE_INTERNO', 'AUDITORIA'].includes(user?.role);

  const verifyChain = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await api.get('/audit-logs/verify-chain');
      setVerifyResult(res.data);
      if (res.data.chain_status === 'VALID') {
        toast.success(`Cadeia íntegra — ${res.data.verified_events} evento(s) verificado(s)`);
      } else {
        toast.error(`Cadeia COMPROMETIDA — ${res.data.invalid_events} evento(s) inválido(s)`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao verificar cadeia');
    } finally {
      setVerifying(false);
    }
  };

  const fetchLogs = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('skip', String(page * 50));
      params.append('limit', '50');

      if (filters.entity_type) params.append('entity_type', filters.entity_type);
      if (filters.action) params.append('action', filters.action);
      if (filters.user_id) params.append('user_id', filters.user_id);
      if (filters.entity_id) params.append('entity_id', filters.entity_id);

      const response = await api.get(`/audit-logs?${params.toString()}`);
      setLogs(response.data);
      setHasMore(response.data.length === 50);
    } catch (error) {
      console.error('Error fetching audit logs:', error);
      toast.error('Erro ao carregar logs de auditoria');
    } finally {
      setLoading(false);
    }
  }, [api, page, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const clearFilters = () => {
    setFilters({
      entity_type: '',
      action: '',
      user_id: '',
      entity_id: ''
    });
    setPage(0);
  };

  const activeFiltersCount = Object.values(filters).filter(v => v).length;

  const getActionBadgeColor = (action) => {
    switch (action) {
      case 'CREATE': return 'bg-green-100 text-green-800';
      case 'UPDATE': return 'bg-blue-100 text-blue-800';
      case 'TRANSITION': return 'bg-blue-100 text-blue-800';
      case 'UPLOAD': return 'bg-cyan-100 text-cyan-800';
      case 'VALIDATE': return 'bg-orange-100 text-orange-800';
      default: return 'bg-slate-100 text-slate-800';
    }
  };

  const getEntityLabel = (entityType) => {
    const entity = ENTITY_TYPES.find(e => e.value === entityType);
    return entity?.label || entityType;
  };

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="audit-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Auditoria</h1>
            <p className="text-slate-500 mt-1">
              Registro completo de todas as ações realizadas no sistema
            </p>
          </div>
          <div className="flex items-center gap-2">
            {canVerifyChain && (
              <Button
                onClick={verifyChain}
                disabled={verifying}
                className="bg-blue-700 hover:bg-blue-800 text-white"
                data-testid="verify-chain-btn"
              >
                {verifying ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <ShieldCheck className="w-4 h-4 mr-2" />
                )}
                Verificar Integridade da Auditoria
              </Button>
            )}
            <ExportButton
              data={logs}
              columns={COLUMN_PRESETS.auditLogs}
              title="Trilha de Auditoria"
              filename="auditoria-bluegov"
              tenant={user?.tenant_id}
              generatedBy={user?.name}
              variant="outline"
              className="border-slate-300"
            />
          </div>
        </div>

        {verifyResult && (
          <Card
            className={`border-2 ${
              verifyResult.chain_status === 'VALID'
                ? 'border-emerald-300 bg-emerald-50'
                : 'border-red-300 bg-red-50'
            }`}
            data-testid="verify-chain-result"
          >
            <CardContent className="pt-5 pb-5">
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
                    verifyResult.chain_status === 'VALID'
                      ? 'bg-emerald-600 text-white'
                      : 'bg-red-600 text-white'
                  }`}
                >
                  <ShieldCheck className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3
                    className={`text-lg font-bold ${
                      verifyResult.chain_status === 'VALID' ? 'text-emerald-800' : 'text-red-800'
                    }`}
                    data-testid="verify-chain-status"
                  >
                    {verifyResult.chain_status === 'VALID'
                      ? 'Cadeia de auditoria íntegra (SHA-256)'
                      : 'Cadeia COMPROMETIDA — adulteração detectada'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Total</p>
                      <p className="font-bold text-slate-900">{verifyResult.total_events}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Verificados</p>
                      <p className="font-bold text-emerald-700">{verifyResult.verified_events}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Inválidos</p>
                      <p className="font-bold text-red-700">{verifyResult.invalid_events}</p>
                    </div>
                    <div>
                      <p className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Verificado em</p>
                      <p className="font-mono text-xs text-slate-700">
                        {new Date(verifyResult.verified_at).toLocaleString('pt-BR')}
                      </p>
                    </div>
                  </div>
                  {verifyResult.first_invalid && (
                    <div className="mt-3 p-3 bg-white border border-red-200 rounded-lg text-xs">
                      <p className="font-bold text-red-800 mb-1">Primeiro evento inválido:</p>
                      <p className="font-mono text-slate-600">log_id: {verifyResult.first_invalid.log_id}</p>
                      <p className="font-mono text-slate-600">chain_index: {verifyResult.first_invalid.chain_index}</p>
                      <ul className="list-disc pl-5 mt-1">
                        {verifyResult.first_invalid.problems.map((p, i) => (
                          <li key={i} className="text-red-700">{p}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setVerifyResult(null)}
                  className="text-slate-500"
                  data-testid="verify-chain-close"
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por ID da entidade..."
                className="pl-10"
                value={filters.entity_id}
                onChange={(e) => setFilters({ ...filters, entity_id: e.target.value })}
                data-testid="search-entity-input"
              />
            </div>
            <Button
              variant={showFilters ? 'default' : 'outline'}
              onClick={() => setShowFilters(!showFilters)}
              className={showFilters ? 'bg-[#1d4ed8]' : ''}
              data-testid="toggle-filters-btn"
            >
              <Filter className="w-4 h-4 mr-2" />
              Filtros
              {activeFiltersCount > 0 && (
                <Badge className="ml-2 bg-white text-[#1d4ed8]">{activeFiltersCount}</Badge>
              )}
            </Button>
          </div>

          {showFilters && (
            <div className="mt-4 pt-4 border-t border-slate-200 animate-fade-in">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  value={filters.entity_type}
                  onValueChange={(v) => setFilters({ ...filters, entity_type: v })}
                >
                  <SelectTrigger data-testid="filter-entity-type">
                    <SelectValue placeholder="Tipo de Entidade" />
                  </SelectTrigger>
                  <SelectContent>
                    {ENTITY_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select
                  value={filters.action}
                  onValueChange={(v) => setFilters({ ...filters, action: v })}
                >
                  <SelectTrigger data-testid="filter-action">
                    <SelectValue placeholder="Ação" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTIONS.map((action) => (
                      <SelectItem key={action.value} value={action.value}>{action.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Input
                  placeholder="ID do Usuário"
                  value={filters.user_id}
                  onChange={(e) => setFilters({ ...filters, user_id: e.target.value })}
                  data-testid="filter-user-id"
                />
              </div>

              {activeFiltersCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="mt-4 text-slate-500"
                  onClick={clearFilters}
                  data-testid="clear-filters-btn"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar filtros
                </Button>
              )}
            </div>
          )}
        </Card>

        {/* Logs Table */}
        <Card>
          {loading ? (
            <div className="p-6 space-y-4">
              {[...Array(10)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="empty-state py-16">
              <FileText className="empty-state-icon" />
              <h3 className="empty-state-title">Nenhum log encontrado</h3>
              <p className="empty-state-description">
                Não foram encontrados registros de auditoria com os filtros selecionados
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table className="data-table">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-40">Data/Hora</TableHead>
                    <TableHead>Usuário</TableHead>
                    <TableHead>Ação</TableHead>
                    <TableHead>Entidade</TableHead>
                    <TableHead>ID</TableHead>
                    <TableHead className="w-48">Alterações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.log_id} data-testid={`audit-row-${log.log_id}`}>
                      <TableCell className="font-mono text-xs text-slate-600">
                        {format(new Date(log.timestamp), 'dd/MM/yyyy HH:mm:ss', { locale: ptBR })}
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="text-sm text-slate-900 truncate max-w-[150px]">{log.user_email}</p>
                          <p className="text-xs font-mono text-slate-400">{log.user_id}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={getActionBadgeColor(log.action)}>
                          {ACTIONS.find(a => a.value === log.action)?.label || log.action}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-slate-600">
                        {getEntityLabel(log.entity_type)}
                      </TableCell>
                      <TableCell className="font-mono text-xs text-slate-500">
                        {log.entity_id}
                      </TableCell>
                      <TableCell>
                        {log.previous_state && log.new_state ? (
                          <div className="text-xs">
                            {Object.keys(log.new_state).slice(0, 2).map((key) => (
                              <div key={key} className="truncate">
                                <span className="text-slate-400">{key}:</span>{' '}
                                <span className="text-slate-600">{String(log.new_state[key]).substring(0, 20)}</span>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-slate-400 text-xs">-</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Pagination */}
          {!loading && logs.length > 0 && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-slate-200">
              <p className="text-sm text-slate-500">
                Página {page + 1}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => p + 1)}
                  disabled={!hasMore}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </Card>
      </div>
    </AppLayout>
  );
};

export default AuditPage;
